
import Vue from "vue";
import { mapState, mapMutations } from "vuex";
import { constants } from "@/utils/constants";
import { logout as amplifyLogout } from "@/auth/client";
import { paginateTable } from "@/interfaces/Additional.interfaces";

export default Vue.extend({
  name: "UnifiedBar",
  props: {
    showNavToggle: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    ...mapState(["preparedDataToSave", "tablename"]),

    isUsingReadReplica(): boolean {
      const databaseSource = localStorage.getItem("databaseSource");
      return databaseSource === "readReplica";
    },

    userFirstName(): string {
      // Try to get first name from JWT token (Cognito often includes given_name)
      try {
        const cognitoIdToken = localStorage.getItem("cognitoIdToken");
        if (cognitoIdToken) {
          const payload = cognitoIdToken.split(".")[1];
          const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
          const decoded = JSON.parse(atob(normalized));
          
          // Cognito JWT tokens often have given_name or name
          if (decoded.given_name) {
            return decoded.given_name;
          }
          if (decoded.name) {
            // If name is "First Last", extract first name
            const nameParts = decoded.name.trim().split(/\s+/);
            return nameParts[0] || "";
          }
        }
      } catch (error) {
        // If JWT parsing fails, fall through to email extraction
      }

      // Fallback: Extract first name from email (part before @)
      try {
        const userEmail = localStorage.getItem("user");
        if (userEmail) {
          // Handle if userEmail is JSON string
          let email = userEmail;
          try {
            const parsed = JSON.parse(userEmail);
            email = typeof parsed === "string" ? parsed : parsed.email || parsed.username || userEmail;
          } catch {
            // Not JSON, use as is
          }

          // Extract the part before @ and capitalize first letter
          const emailPrefix = email.split("@")[0];
          // Handle dots, underscores, and numbers - take the first meaningful part
          const firstName = emailPrefix.split(/[._]/)[0];
          return firstName.charAt(0).toUpperCase() + firstName.slice(1).toLowerCase();
        }
      } catch (error) {
        console.warn("Failed to extract user first name:", error);
      }

      return "";
    },

    pageGroupLabel(): string {
      const route = this.$route;
      const path = route.path;
      const name = route.name;

      // TALENT PAGE GROUP
      if (path === "/talent") {
        return "Talent";
      }
      if (
        path.includes("/tables/Hygienist") ||
        path.includes("/membership-integrity/MemberReview") ||
        path.includes("/membership-integrity/FirstShiftBooked") ||
        path.includes("/disputes") ||
        path.includes("/tables/BlockedHygienist") ||
        path.includes("/tables/FavouriteHygienist") ||
        path.includes("/Resumes") ||
        path.includes("/tables/User")
      ) {
        return "Talent";
      }

      // DENTAL OFFICES PAGE GROUP
      if (path === "/dental-offices") {
        return "Dental Offices";
      }
      if (
        path.includes("/tables/Location") ||
        path.includes("/tables/BlockedDentist") ||
        path.includes("/tables/FavouriteDentist") ||
        path.includes("/HireJobs")
      ) {
        return "Dental Offices";
      }

      // CONNECTIONS PAGE GROUP
      if (
        path.includes("/tables/DailyShiftList") ||
        path.includes("/tables/Job") ||
        path.includes("/tables/Shift") ||
        path.includes("/tables/Invoice") ||
        path.includes("/payment-integrity") ||
        path.includes("/tables/SNCBillingBoard") ||
        path.includes("/SNCbyHygDa") ||
        path.includes("/chat-history")
      ) {
        return "Connections";
      }

      // ADMIN TOOLS PAGE GROUP
      if (
        path.includes("/history") ||
        path.includes("/review-history") ||
        path.includes("/notes") ||
        path.includes("/Reliable")
      ) {
        return "Admin Tools";
      }

      // HOME PAGE
      if (path === "/" || path === "/home") {
        return "Dashboard";
      }

      // SPECIFIC PAGE NAMES for other routes
      const pageNameMap: Record<string, string> = {
        login: "Login",
        authcallback: "Authentication"
      };

      if (name && pageNameMap[name as string]) {
        return pageNameMap[name as string];
      }

      // Default fallback
      return "Admin";
    }
  },
  methods: {
    ...mapMutations(["modify", "callSnackbar"]),

    callDialog() {
      this.modify({ property: "confirmDialogShow", value: true });
    },

    async restore() {
      this.modify({ property: "preparedDataToSave", value: [] });

      try {
        if (constants.serverPaginateTables.includes(this.tablename)) {
          await this.$store.dispatch("getPaginateData", {
            tableName: this.tablename,
            limit: 15,
            page: 1,
            columnNames:
              constants.enabledColumns[this.tablename.toLowerCase() + "Table"]
          } as paginateTable);
        } else {
          await this.$store.dispatch("getData", { tablename: this.tablename });
        }

        this.callSnackbar({
          visible: true,
          text: "Data restored",
          color: "green darken-2"
        });
      } catch (error) {
        this.callSnackbar({
          visible: true,
          text: "Error",
          color: "red darken-2"
        });
      }
    },

    async logout() {
      try {
        await amplifyLogout();
      } catch (error) {
        console.warn(
          "🚪 Logout: Amplify signOut failed or not configured:",
          error
        );
      }
      localStorage.clear();
      sessionStorage.clear();
      // T2-6344: clear App.vue's reactive `auth` flag so the menu hides on
      // logout even for password/direct-login admins (no Cognito Hub event).
      this.$root.$emit("appSignOut");
      this.$router.push("/login");
    },

    switchToStagingDb() {
      localStorage.setItem("databaseSource", "primary");
      window.location.reload();
    }
  }
});
